import Vue from "vue";
import gql from "graphql-tag";
import store from "@/store";
import OrderQueries from "@/graphql/OrderQueries";

export default {
  namespaced: false,
  state: () =>  ({
    currentCart: null,
    cartOwner: null,
    cartData: null,
    cartDataLoading: false,
    cartDataLoadedFor: null,
    cartDataErrorFor: null,
    cartDataStale: false,
    bookCache: {},
    buyNowOrder: null,
    buyNowBook: null,
  }),
  getters: {
    currentCart: (state) => state.currentCart,
    cartOwner: (state) => state.cartOwner,
    cartData: (state) => state.cartData,
    cartDataLoading: (state) => state.cartDataLoading,
    cartDataLoadedFor: (state) => state.cartDataLoadedFor,
    cartDataErrorFor: (state) => state.cartDataErrorFor,
    cartDataStale: (state) => state.cartDataStale,
    cachedBook: (state) => (productId) => state.bookCache[productId] || null,
    buyNowOrder: (state) => state.buyNowOrder,
    buyNowBook: (state) => state.buyNowBook,
  },
  mutations: {
    setCurrentCart: (state, newCart) => {
      const cartChanged = state.currentCart !== newCart;
      state.currentCart = newCart;
      if (!newCart || cartChanged) {
        state.cartData = null;
        state.cartDataLoadedFor = null;
        state.cartDataErrorFor = null;
        state.cartDataStale = false;
      }
    },
    setCartOwner: (state, newCartOwner) => state.cartOwner = newCartOwner,
    setCartData: (state, data) => {
      state.cartData = data;
      state.cartDataLoadedFor = state.currentCart;
      state.cartDataErrorFor = null;
      state.cartDataStale = false;
    },
    markCartDataStale: (state) => {
      state.cartDataStale = true;
    },
    setCartDataLoading: (state, isLoading) => state.cartDataLoading = isLoading,
    setCartDataErrorFor: (state, orderId) => state.cartDataErrorFor = orderId,
    setBuyNowOrder: (state, orderId) => state.buyNowOrder = orderId,
    setBuyNowBook: (state, book) => state.buyNowBook = book,
    clearBuyNow: (state) => {
      state.buyNowOrder = null;
      state.buyNowBook = null;
    },
    cacheBook: (state, { productId, data }) => {
      const existing = state.bookCache[productId];
      if (
        existing &&
        existing.thumbnailUrl === data.thumbnailUrl &&
        JSON.stringify(existing.authors) === JSON.stringify(data.authors)
      ) {
        return;
      }
      Vue.set(state.bookCache, productId, data);
    },
  },
  actions: {
    async handleCartOnLogin({dispatch}, apolloClient) {
      if (store.state.cart.currentCart && !store.state.cart.cartOwner) {
        dispatch("claimOrder", apolloClient);
      }
      else if (store.state.auth.getMe && !store.state.cart.cartOwner) {
        dispatch("getOpenCart", apolloClient);
      }
    },
    async claimOrder({ commit }, apolloClient) {
      try {
        const response = await apolloClient.mutate({
          mutation: gql`
            mutation ClaimOrder($orderId: ID!) {
              claimOrder(input: { id: $orderId }) {
                order {
                  id
                  customerId
                  orderLines(first: 1000) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            orderId: store.state.cart.currentCart,
          }
        });

        if (response.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }

        if (response.data) {
          commit('setCartOwner', response.data.claimOrder.order.customerId);
        }
      }
      catch (e) {
        console.error('%cError in claiming order (login)', 'color: red;', e.message)
        commit('setCurrentCart', null)
      }
    },
    async getOpenCart({ commit }, apolloClient) {
      try {
        const response = await apolloClient.query({
          query: gql`
            query GetMyOrders {
              getMyOrders(confirmed: false) {
                id
              }
            }
          `,
        });

        if (response.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }

        if (response.data) {
          const firstOrder = response.data.getMyOrders.find((order) => order.id);
          if (firstOrder) {
            commit("setCurrentCart", firstOrder.id);
            commit('setCartOwner', store.state.auth.getMe);
          }
        }
      }
      catch (e) {
        console.error('%cError in getting open order (login)', 'color: red;', e.message)
        commit('setCurrentCart', null)
      }
    },
    async fetchCartData({ commit, state }, { apolloClient, forceNetwork = false } = {}) {
      if (!state.currentCart || !apolloClient) {
        return null;
      }

      if (state.cartDataLoading) {
        return state.cartData;
      }

      if (!forceNetwork && state.cartDataErrorFor === state.currentCart) {
        return null;
      }

      if (!forceNetwork && state.cartData && state.cartDataLoadedFor === state.currentCart) {
        return state.cartData;
      }

      commit("setCartDataLoading", true);

      try {
        const response = await apolloClient.query({
          query: OrderQueries.getOrder,
          variables: {
            orderId: state.currentCart,
          },
          fetchPolicy: forceNetwork ? "network-only" : "cache-first",
        });

        const order = response.data?.order || null;

        if (!order) {
          commit("setCurrentCart", null);
          return null;
        }

        commit("setCartData", order);
        return order;
      }
      catch (e) {
        console.error('%cError in fetching cart data', 'color: red;', e.message)
        commit("setCartDataErrorFor", state.currentCart);
        return null;
      }
      finally {
        commit("setCartDataLoading", false);
      }
    }
  },
}
